/**
 * 房间壳层的纯派生函数。零 React、零 DOM、零 store —— 全部可在 node 环境单测。
 *
 * 单独成文件的理由与 `table/tableDerive.ts` 一样：这些规则（谁能开局、座位该显示
 * 什么状态、断线条要不要弹）是最容易在改 UI 时被悄悄改错的部分，必须能被测试钉死。
 */
import type {
  ChatEntry,
  ClientMessage,
  PlayerView,
  PublicHand,
  PublicSeat,
  ResumeSummary,
  RoomConfig,
  SeatIndex,
  TopUpRequestView,
} from '@poker/shared'

import type { ConnectionPhase } from '../store/tableStore.js'
import type { NetFatal } from '../transport/applyMessage.js'

// ============================================================================
// 座位
// ============================================================================

export type SeatBadgeTone =
  | 'acting'
  | 'folded'
  | 'allin'
  | 'offline'
  | 'sitout'
  | 'host'
  | 'bot'

export interface SeatBadge {
  readonly text: string
  readonly tone: SeatBadgeTone
}

/**
 * 座位状态角标。**顺序即优先级**，第一个是最该被一眼看到的。
 *
 * 每个状态都是「文字 + 颜色」两重编码（§11.10）：单靠颜色的话，
 * 深色模式 + 色弱用户会分不清「弃牌灰」和「坐出灰」。
 *
 * 用词与牌桌上的座位胶囊同一套（UI-SPEC「入局」的座位角标表）：
 *   · 断线 / 挂机都说「离线」，与牌桌胶囊一致；
 *   · 暂离只有这一个词（COPY.md 第 6 条），`awayUser` 以前写成「离开」，与离开房间撞词；
 *   · 筹码为 0 说「筹码用完」，买分在途说「买分中」。以前写「已出局」——能买分的房间里没人出局；
 *   · 房间规则里没有等大盲，那个角标删了。
 *
 * `topUps` 用来区分「筹码用完」与「买分中」，一般传 `view.topUps ?? []`。
 */
export function seatBadges(
  seat: PublicSeat,
  hand: PublicHand | null,
  topUps: readonly TopUpRequestView[] = [],
): SeatBadge[] {
  const occupant = seat.occupant
  if (occupant === null) return []
  const out: SeatBadge[] = []
  const inHand = hand !== null && seat.inHand
  // 本手还拿着牌（全下时筹码也是 0）不算「筹码用完」
  const broke = seat.stack <= 0 && !(inHand && hand.phase !== 'complete')

  if (inHand && hand.actorSeat === seat.index) out.push({ text: '行动中', tone: 'acting' })
  if (inHand && seat.handState === 'allIn') out.push({ text: 'ALL IN', tone: 'allin' })
  if (inHand && seat.handState === 'folded') out.push({ text: '已弃牌', tone: 'folded' })

  switch (seat.presence) {
    case 'disconnected':
    case 'idleTimeout':
      // 与牌桌上的座位胶囊同一个词（applyMessage.seatNoteOf 把这两种都画成「离线」）：
      // 以前名单写「断线 / 挂机」、牌桌写「离线」，同一个人两处两种说法
      out.push({ text: '离线', tone: 'offline' })
      break
    case 'awayUser':
    case 'sittingOut':
      // 输光的人服务端顺手置成了暂离，可他缺的是筹码：下面说「筹码用完 / 买分中」，这里不再说暂离
      if (!broke) out.push({ text: '暂离', tone: 'sitout' })
      break
    default:
      break
  }

  if (broke) {
    const buying = topUps.some((r) => r.seat === seat.index && r.accountId === occupant.accountId)
    out.push({ text: buying ? '买分中' : '筹码用完', tone: 'sitout' })
  } else if (seat.sitOutNextHand && seat.presence !== 'sittingOut' && seat.presence !== 'awayUser') {
    // 「下一手坐出」与「已经坐出」是两件事：前者这手还在打，混显会让人以为自己被踢了
    out.push({ text: '下手暂离', tone: 'sitout' })
  }
  if (occupant.isBot) out.push({ text: 'AI', tone: 'bot' })
  if (occupant.isHost) out.push({ text: '房主', tone: 'host' })
  return out
}

export type SeatPositionMark = 'D' | 'SB' | 'BB' | null

/** 庄家钮 / 盲注标记。单挑时按钮兼小盲，此时只标 D（避免一个座位挂两个牌子）。 */
export function seatPositionMark(seat: SeatIndex, hand: PublicHand | null): SeatPositionMark {
  if (hand === null) return null
  if (hand.buttonSeat === seat) return 'D'
  if (hand.sbSeat === seat) return 'SB'
  if (hand.bbSeat === seat) return 'BB'
  return null
}

/** 已入座的座位（含 AI），按座位号升序。 */
export function occupiedSeats(view: PlayerView | null): PublicSeat[] {
  if (view === null) return []
  return view.seats.filter((s) => s.occupant !== null).sort((a, b) => a.index - b.index)
}

/** 空座位号。`sitDown` 只能选这些。 */
export function emptySeatIndexes(view: PlayerView | null): SeatIndex[] {
  if (view === null) return []
  return view.seats.filter((s) => s.occupant === null).map((s) => s.index)
}

/**
 * 牌局进行中，底部空闲条该给哪一颗「回到牌桌」的按钮。
 *
 * ★ 这是 #6「牌局开始后没法坐下」与 #7「被判坐出后没有回桌入口」的共同修复：
 *   全客户端原来唯一的入座 / 回桌入口都在等待室里，而等待室只在 lobby / paused 相位渲染；
 *   服务端的 sitDown / setSitOutNextHand 本来就没有相位限制 —— 能力有，入口没有。
 *
 * - 'sit'：自己没座位、且有空位 → 打开选座面板（新人按 newPlayerPolicy 等下一手）
 * - 'return'：自己坐出（系统判的或自己点的）且**还有筹码** → 发 setSitOutNextHand(false)
 *   筹码为 0 时**不给**这颗按钮：服务端 sitIn 会静默拒掉（#14 踩过），该显示的是买分胶囊。
 * - lobby / paused 一律 null：等待室已经有更完整的主 CTA，这里再给一颗只是噪音。
 */
export type IdleDockAction = 'sit' | 'return' | null

export function idleDockAction(view: PlayerView | null): IdleDockAction {
  if (view === null || view.room.phase !== 'running') return null
  const hero = heroSeatOf(view)
  if (hero === null) return emptySeatIndexes(view).length > 0 ? 'sit' : null
  const seat = view.seats.find((s) => s.index === hero)
  if (seat === undefined) return null
  const sitOut = seat.presence === 'sittingOut' || seat.sitOutNextHand
  return sitOut && seat.stack > 0 ? 'return' : null
}

/**
 * 牌局进行中、这一手没发到我时，状态行该说什么（null = 用默认的「等待 X 行动」）。
 *
 * ★ 以前这里什么都不说：被暂离、筹码用完，界面上一律是「等待其他玩家行动」，
 *   用户只能理解成「我被轮空了」。按「此刻最该知道的一件事」排优先级，只说一句。
 * ★ 短：360 宽的空闲条上还有「回到牌局」按钮。同一件事只说一次：按钮上写着「回到牌局」，
 *   这里就不再说「点回到牌局」；买分进度由舞台上的胶囊说，这里只说「筹码用完了」。
 * ★ 房间规则：回来 / 新坐下的人下一手就发牌，没有「等大盲」。
 */
export function heroIdleNote(view: PlayerView | null): string | null {
  if (view === null || view.room.phase !== 'running') return null
  const hero = heroSeatOf(view)
  if (hero === null) return null
  const seat = view.seats.find((s) => s.index === hero)
  if (seat === undefined || seat.occupant === null) return null
  // 这一手已经发到我了：照常显示「等待 X 行动」
  const hand = view.hand
  const handLive = hand !== null && hand.phase !== 'complete'
  if (handLive && seat.inHand) return null
  if (seat.stack <= 0) return '筹码用完了'
  if (seat.presence === 'sittingOut' || seat.sitOutNextHand) return '已暂离'
  // 正常在局、只是这一手开局时还不在（刚回来 / 刚到账）：下一手就发
  if (handLive) return '下一手发牌'
  return null
}

// ============================================================================
// 「我的座位」面板
// ============================================================================

/**
 * 面板第一行做什么（四选一，永远只有一行）：
 *   sitOut       「暂离」—— 这手发到了牌就排到这手打完
 *   cancelSitOut 「取消暂离」—— 刚点了暂离、这手还没打完
 *   return       「回到牌局」—— 已暂离且有筹码；★ 面板里唯一的 Primary
 *   topUp        「买分」—— 筹码用完了：回到牌局在服务端是空操作，该做的是买分
 */
export type SeatSelfRest = 'sitOut' | 'cancelSitOut' | 'return' | 'topUp'

export interface SeatSelfModel {
  readonly seat: SeatIndex
  readonly rest: SeatSelfRest
  /** 「换座」为什么点不了（12px 小字写在行内）；null = 能换 */
  readonly changeBlocked: string | null
  /** 已经点了起立、在等这手打完 → 第三行是灰掉的「这手打完起立」 */
  readonly standPending: boolean
  /** 这一手发到了我（弃了也算）：暂离 / 起立都要排到这手打完 */
  readonly dealtIn: boolean
}

/**
 * 「我的座位」面板该显示什么；不该有这个面板（旁观、没快照、牌局已结算）时为 null。
 *
 * ★ 换座的判据与服务端 onChangeSeat 同一口径（D7）：本手没发到牌就能换，越过盲注也行 ——
 *   房间规则里回来 / 新坐下的人下一手照常发牌，不补盲、不等大盲，换座同理。
 *   买分 / 转账在途时服务端也会拒（申请按座位认人，换座会让它们作废），界面先挡住并说清。
 */
export function seatSelfActions(view: PlayerView | null): SeatSelfModel | null {
  if (view === null) return null
  if (view.settlement !== undefined || view.room.phase === 'closed') return null
  const hero = heroSeatOf(view)
  if (hero === null) return null
  const seat = view.seats.find((s) => s.index === hero)
  if (seat === undefined || seat.occupant === null) return null

  const hand = view.hand
  const dealtIn = hand !== null && hand.phase !== 'complete' && seat.inHand
  const rest: SeatSelfRest =
    seat.stack <= 0 && !dealtIn
      ? 'topUp'
      : seat.presence === 'sittingOut'
        ? 'return'
        : seat.sitOutNextHand
          ? 'cancelSitOut'
          : 'sitOut'

  const topUpInFlight = (view.topUps ?? []).some((r) => r.seat === hero)
  const transferInFlight = (view.transfers ?? []).some((r) => r.fromSeat === hero || r.toSeat === hero)
  const changeBlocked =
    dealtIn || seat.leaving !== null
      ? '这手打完再换'
      : topUpInFlight
        ? '买分处理完再换'
        : transferInFlight
          ? '转账处理完再换'
          : emptySeatIndexes(view).length === 0
            ? '没有空位'
            : null

  return { seat: hero, rest, changeBlocked, standPending: seat.leaving === 'seat', dealtIn }
}

/** 面板上每一行的意图。前三个直接发消息，其余是本地导航（开别的面板 / 离开房间）。 */
export type SeatSelfIntent = SeatSelfRest | 'change' | 'stand' | 'leave'

/**
 * 意图 → 上行消息；null = 不发消息（开买分面板 / 开换座面板 / 离开房间走 useLeaveRoom）。
 * ★ 红线 #37：一律不带座位，自己的座位由连接身份解析。
 */
export function seatSelfMessage(intent: SeatSelfIntent): ClientMessage | null {
  switch (intent) {
    case 'sitOut':
      return { type: 'setSitOutNextHand', sitOut: true }
    case 'cancelSitOut':
    case 'return':
      return { type: 'setSitOutNextHand', sitOut: false }
    case 'stand':
      return { type: 'standUp' }
    case 'topUp':
    case 'change':
    case 'leave':
      return null
  }
}

/**
 * 点完之后要不要冒一句话。只有「排到这手打完」的两件事要说：面板一关，牌桌上什么都没变
 * （人还在打这手），不说的话会以为没点上。立刻生效的不说 —— 座位、空闲条自己就变了。
 */
export function seatSelfFeedback(intent: SeatSelfIntent, model: SeatSelfModel): string | null {
  if (!model.dealtIn) return null
  if (intent === 'sitOut') return '这手打完暂离'
  if (intent === 'stand') return '这手打完起立'
  return null
}

/**
 * 顶栏「离开」是打开「我的座位」面板，还是两段式离开房间。
 * 坐着、这一页有行动权 → 面板；旁观者、影子页（没有行动权，面板里的事服务端都会拒）→ 两段式。
 */
export function leaveOpensSeatSheet(view: PlayerView | null, isActor: boolean): boolean {
  return isActor && seatSelfActions(view) !== null
}

/** 选座面板点了空座之后发什么：旁观者坐下是 sitDown，已经坐着的是 changeSeat（红线 #37：都只带目标座位） */
export function seatPickMessage(mode: 'sit' | 'change', seat: SeatIndex): ClientMessage {
  return mode === 'change' ? { type: 'changeSeat', seat } : { type: 'sitDown', seat }
}

export function seatedCount(view: PlayerView | null): number {
  return occupiedSeats(view).length
}

export function botCount(view: PlayerView | null): number {
  return occupiedSeats(view).filter((s) => s.occupant?.isBot === true).length
}

export function humanCount(view: PlayerView | null): number {
  return occupiedSeats(view).filter((s) => s.occupant?.isBot === false).length
}

export function isHost(view: PlayerView | null): boolean {
  return view?.viewer.isHost ?? false
}

/** 我坐在哪。旁观者为 null。 */
export function heroSeatOf(view: PlayerView | null): SeatIndex | null {
  return view?.viewer.ownedSeat ?? null
}

// ============================================================================
// 开局条件
// ============================================================================

export interface StartGateResult {
  readonly canStart: boolean
  /** 不能开局的原因文案；能开局时为 null */
  readonly reason: string | null
  /** 按钮文案。暂停中要说「继续」，说「开始」会让人以为要重开一局 */
  readonly label: string
}

/**
 * 座位的「开局可用度」，与服务端 `canStartHand()`（packages/server/src/room/types.ts）**同构**。
 *
 * ★ 为什么必须同构：房主点「继续牌局」时，服务端只是把 phase 置成 running；
 *   真正能不能发牌由 `canStartHand` 再判一次，判不过就立刻又挂回 paused。
 *   前端如果只数「坐了几个人」，就会出现**点了毫无反应**（两个人都处于坐出状态时
 *   100% 复现）—— 用户亲自指出的那个 bug。所以这里逐条照抄服务端的排除规则，
 *   好让按钮在点之前就说清楚「为什么不能开」。
 */
export interface SeatReadiness {
  /** 已入座（含 AI） */
  readonly seated: number
  /** 服务端口径下能参与下一手的座位数 */
  readonly playable: number
  /** 坐出 / 离开 / 挂机（自己点「回到牌局」就能解除） */
  readonly resting: number
  /** 筹码为 0（要补码才能继续） */
  readonly broke: number
  /** 在线的真人（非 AI），且本身可参与下一手 */
  readonly onlineHumans: number
}

/** presence 里哪些算「人不在牌局中」。与服务端一致：只有 online / disconnected 能参与。 */
function isResting(seat: PublicSeat): boolean {
  return (
    seat.sitOutNextHand ||
    seat.presence === 'sittingOut' ||
    seat.presence === 'awayUser' ||
    seat.presence === 'idleTimeout'
  )
}

export function seatReadiness(view: PlayerView | null): SeatReadiness {
  const seats = occupiedSeats(view)
  let playable = 0
  let resting = 0
  let broke = 0
  let onlineHumans = 0
  for (const s of seats) {
    // 排除顺序照抄服务端：先筹码、再坐出、最后 presence
    if (s.stack <= 0) {
      broke++
      continue
    }
    if (isResting(s)) {
      resting++
      continue
    }
    const bot = s.occupant?.isBot === true
    // 断线的真人仍算 playable：服务端给了宽限期，牌局照开（他会被自动弃牌）
    if (!bot && s.presence !== 'online' && s.presence !== 'disconnected') continue
    playable++
    if (!bot && s.presence === 'online') onlineHumans++
  }
  return { seated: seats.length, playable, resting, broke, onlineHumans }
}

/**
 * 「开始游戏」能不能按。
 *
 * 人数下限取 `max(2, minPlayersToStart)`：配置理论上可以被写成 1，
 * 但一个人的德州扑克不是牌局，前端这一层直接兜住而不是等服务端报错。
 *
 * `paused` 与 `lobby` 走同一条判据：房间因为人不够而挂起时，玩家看到的
 * 也该是同一块「还差几人」的信息，而不是两套说法。
 *
 * ★ reason 永远要能回答「我现在该做什么」：是再叫一个人来，还是让已经坐出的人
 *   点「回到牌局」，还是有人筹码打光了要补码。只说「不能开始」等于没说。
 */
export function startGate(view: PlayerView | null): StartGateResult {
  const label = view?.room.phase === 'paused' ? '继续牌局' : '开始游戏'
  if (view === null) return { canStart: false, reason: '尚未连接', label }
  if (!view.viewer.isHost) return { canStart: false, reason: '等待房主开始', label }
  if (view.room.phase !== 'lobby' && view.room.phase !== 'paused') {
    return { canStart: false, reason: null, label }
  }
  const need = Math.max(2, view.room.config.minPlayersToStart)
  const r = seatReadiness(view)

  if (r.seated < need) {
    return { canStart: false, reason: `还差 ${need - r.seated} 人坐下`, label }
  }
  if (r.playable < need) {
    // 人都在，只是没在「牌局里」——这正是点了没反应的那种情况，必须说清
    if (r.resting > 0) {
      return {
        canStart: false,
        reason: `${r.resting} 人暂离中，等他们回到牌局`,
        label,
      }
    }
    if (r.broke > 0) {
      return { canStart: false, reason: `${r.broke} 人筹码为 0，补码后才能继续`, label }
    }
    return { canStart: false, reason: `还差 ${need - r.playable} 人`, label }
  }
  if (!view.room.config.allowBotOnly && r.onlineHumans < 1) {
    return { canStart: false, reason: '至少要有 1 个真人在线，AI 之间不会开局', label }
  }
  return { canStart: true, reason: null, label }
}

/**
 * 房主的主按钮要不要换成「处理买分」。
 *
 * ★ 以前这时候是一颗灰掉的「继续牌局」，下面一行「1 人筹码为 0，补码后才能继续」——
 *   可补码要房主批，而批的入口是顶栏里一个小角标。输光的 AI 现在会自己交申请，
 *   这一幕会变得很常见：能开局的那一步就是批买分，主按钮就该直接去那儿。
 * 只在「人数够、就是开不了」时换：人都没坐够，批一笔买分也开不了局。
 */
export function hostTopUpGate(view: PlayerView | null): boolean {
  if (view === null || !view.viewer.isHost) return false
  if (view.room.phase !== 'lobby' && view.room.phase !== 'paused') return false
  if (startGate(view).canStart) return false
  const need = Math.max(2, view.room.config.minPlayersToStart)
  if (seatReadiness(view).seated < need) return false
  return (view.topUps ?? []).some((r) => r.status === 'pending')
}

/**
 * 房主移出一个座位上的人该发什么。
 *
 * ★ AI 走 `removeBot`，不走 `kickPlayer`：踢人会把账号拉黑，AI 本来就不会回来，拉黑只会在
 *   名单里留一笔；而且 `removeBot` 在局内是「打完这手再走」（`leaving: 'room'`），界面能说清。
 */
export function kickMessage(seat: PublicSeat): ClientMessage | null {
  const occupant = seat.occupant
  if (occupant === null) return null
  return occupant.isBot ? { type: 'removeBot', seat: seat.index } : { type: 'kickPlayer', accountId: occupant.accountId }
}

/**
 * 座位名片上房主能不能「移出」这个 AI。
 *   'remove'  = 能移（局内点了会排到这手打完）
 *   'pending' = 已经点过、在等这手打完 → 灰掉的「这手打完移出」
 *   null      = 不给（不是房主 / 不是 AI / 牌局已结算）
 * 真人不在这里移：踢人会拉黑，是更重的动作，只放在等待室的名单里。
 */
export type BotRemoval = 'remove' | 'pending'

export function botRemoval(view: PlayerView | null, seat: SeatIndex | null): BotRemoval | null {
  if (view === null || seat === null || !view.viewer.isHost) return null
  if (view.settlement !== undefined || view.room.phase === 'closed') return null
  const row = view.seats.find((s) => s.index === seat)
  if (row === undefined || row.occupant === null || !row.occupant.isBot) return null
  return row.leaving === 'room' ? 'pending' : 'remove'
}

/** 点了移出之后要不要冒一句话：AI 这手还在打，面板一关牌桌上什么都没变 */
export function botRemovalFeedback(view: PlayerView | null, seat: SeatIndex): string | null {
  const hand = view?.hand ?? null
  const row = view?.seats.find((s) => s.index === seat)
  const dealtIn = hand !== null && hand.phase !== 'complete' && row?.inHand === true
  return dealtIn ? '这手打完移出' : null
}

/** 还能不能再加 AI（房间允许 + 没超上限 + 有空位）。 */
export function canAddBot(view: PlayerView | null): boolean {
  if (view === null) return false
  const c = view.room.config
  if (!c.botsAllowed) return false
  if (botCount(view) >= c.maxBots) return false
  return emptySeatIndexes(view).length > 0
}

/** 盲注文案。`config.ante` 非 0 时一并显示，否则玩家会在第一手被莫名扣一笔。 */
export function blindsText(config: RoomConfig | null): string {
  if (config === null) return '—'
  const base = `${config.smallBlind}/${config.bigBlind}`
  return config.anteMode === 'none' || config.ante <= 0 ? base : `${base} (前注 ${config.ante})`
}

// ============================================================================
// 断线条（ConnectionBar）
// ============================================================================

/** 短于这个时长的断线不弹条：闪一下比断线本身更烦人。 */
export const OFFLINE_FLASH_GUARD_MS = 3000
/** 「你错过了 N 手牌」显示多久后自动消失。 */
export const RESUME_HINT_MS = 3000

export type ConnectionBarKind = 'none' | 'offline' | 'resumed' | 'fatal'

export interface ConnectionBarModel {
  readonly kind: ConnectionBarKind
  readonly text: string
  /** 次要说明；没有则为 null */
  readonly sub: string | null
  /** 是否显示「立即重试」 */
  readonly showRetry: boolean
  /** 是否显示重连倒计时（自动重连还在跑时才有意义） */
  readonly showCountdown: boolean
}

export const BAR_NONE: ConnectionBarModel = {
  kind: 'none',
  text: '',
  sub: null,
  showRetry: false,
  showCountdown: false,
}

export interface ConnectionBarInput {
  readonly phase: ConnectionPhase
  readonly fatal: NetFatal | null
  readonly resume: ResumeSummary | null
  /** 进入非 live 相位的时刻（本地时钟）；live 时为 null */
  readonly offlineSinceMs: number | null
  readonly now: number
  /** 退避已放弃自动重连 */
  readonly exhausted: boolean
  /**
   * 牌局已经结算（战报在屏幕上）。
   *
   * ★ `endGame` 之后服务端立刻 `closeRoom`，于是断线条会弹出「房间已关闭」——
   *   可这正是房主刚刚亲手做的事，而且战报已经把它说得清清楚楚。
   *   在一份要被截图发群里的战报上方压一条红色告警条，只会让人以为出错了。
   */
  readonly settled?: boolean
}

const PHASE_TEXT: Partial<Record<ConnectionPhase, string>> = {
  connecting: '连接中…',
  // 「登录」「同步」是实现阶段，不是玩家关心的事：三个相位都只说「连接中」
  authenticating: '连接中…',
  syncing: '连接中…',
  degraded: '网络不稳定',
  backoff: '重连中…',
}

/**
 * 断线条要显示什么。纯函数，所有时间都从入参来 —— 于是「3 秒内不闪」这条规则
 * 可以被单测钉死，而不是靠手动拔网线验收。
 */
export function resolveConnectionBar(input: ConnectionBarInput): ConnectionBarModel {
  const { fatal } = input
  // 结算完的关房是预期结果，不是故障：战报自己就是那条「牌局结束」的消息
  if (fatal !== null && fatal.kind === 'roomClosed' && input.settled === true) return BAR_NONE
  if (fatal !== null) {
    return {
      kind: 'fatal',
      text: fatalText(fatal),
      sub: fatal.kind === 'superseded' ? '这台设备不再更新牌局' : null,
      // superseded 绝不自动重连，也不该给「重试」——那等于把另一台设备踢下线
      showRetry: fatal.kind === 'shutdown',
      showCountdown: false,
    }
  }

  /**
   * ★ close(4009) 这条**真·死连接**的路：`onClosed` 只 `setPhase('superseded')`，
   *   不设 fatal（fatal 现在只由 `sessionRevoked` / `serverShutdown` 那几条产生）。
   *   少了这个分支，它会掉进下面的「重连中… / 立即重试」——而重连只会把
   *   另一台设备再踢下线，两台无限对踢。
   *   注意这里的「不再更新牌局」是**真的**：socket 已经关了。
   *   而 `sessionSuperseded` **消息**那条路不走这里（连接还活着，照收广播），
   *   它只让常驻接管条出现。
   */
  if (input.phase === 'superseded') {
    return {
      kind: 'fatal',
      text: '这一桌已在别处打开',
      sub: '这台设备不再更新牌局',
      showRetry: false,
      showCountdown: false,
    }
  }

  if (input.phase === 'live' || input.phase === 'idle') {
    const r = input.resume
    // 没错过任何东西就别打扰：重连成功本身不是一条需要玩家知道的消息
    if (r === null || r.missedHands <= 0) return BAR_NONE
    return {
      kind: 'resumed',
      text: r.headline === '' ? `你错过了 ${r.missedHands} 手牌` : r.headline,
      sub: resumeDetail(r),
      showRetry: false,
      showCountdown: false,
    }
  }

  // ★ 短断线不闪：3 秒内恢复的抖动，玩家根本没察觉，弹条只会制造焦虑
  const since = input.offlineSinceMs
  if (since === null || input.now - since < OFFLINE_FLASH_GUARD_MS) return BAR_NONE

  if (input.exhausted) {
    return {
      kind: 'offline',
      text: '连不上服务器',
      sub: null,
      showRetry: true,
      showCountdown: false,
    }
  }

  return {
    kind: 'offline',
    text: PHASE_TEXT[input.phase] ?? '重连中…',
    sub: null,
    showRetry: true,
    showCountdown: input.phase === 'backoff',
  }
}

function fatalText(f: NetFatal): string {
  switch (f.kind) {
    case 'superseded':
      return f.message === '' ? '已在其他设备上打开' : f.message
    case 'revoked':
      return '登录已失效，重新进一次房间'
    case 'shutdown':
      return '服务器正在重启'
    case 'roomClosed':
      return '房间已关闭'
  }
}

/** 重连摘要的第二行：只说玩家真正关心的三件事（输赢、被自动弃牌、错过自己的回合）。 */
export function resumeDetail(r: ResumeSummary): string | null {
  const parts: string[] = []
  if (r.netChipDelta !== 0) parts.push(r.netChipDelta > 0 ? `+${r.netChipDelta}` : String(r.netChipDelta))
  if (r.autoFolds > 0) parts.push(`自动弃牌 ${r.autoFolds} 次`)
  else if (r.missedYourTurn) parts.push('错过了你的回合')
  return parts.length === 0 ? null : parts.join(' · ')
}

// ============================================================================
// 杂项
// ============================================================================

/** 系统消息与玩家消息分开取，房间内的提示条只关心系统消息。 */
export function latestSystemChat(chat: readonly ChatEntry[]): ChatEntry | null {
  for (let i = chat.length - 1; i >= 0; i--) {
    const e = chat[i]
    if (e !== undefined && e.kind === 'system') return e
  }
  return null
}

/** 房间码分组显示：6 位码拆成 3+3，口头念和手动抄的错误率都会明显下降。 */
export function groupRoomCode(code: string | null): string {
  if (code === null || code.length !== 6) return code ?? '——'
  return `${code.slice(0, 3)} ${code.slice(3)}`
}

/**
 * 点「回大厅」时该不该真的发 `leaveRoom`。
 *
 * ★ 没有行动权的那一页**只做本地导航**。`leaveRoom` 会删掉整个 member、
 *   清空该账号的**全部** session —— 你在手机上正打着牌，电脑上那个早已失去
 *   行动权的旧标签页点一下「回大厅」，手机就被一起拽下了桌。
 *   而 shadow 页上的用户点这个按钮，十有八九只是想关掉这个窗口。
 *
 * 服务端**故意**不给 leaveRoom 设行动权门禁（否则被顶掉的页连退出都做不到），
 * 所以这道判断只能在客户端。
 */
export function leaveRoomMode(isActor: boolean, hasView: boolean): 'send' | 'localOnly' {
  /**
   * ★ 只有**确知自己是影子页**时才走 localOnly。
   *
   * `isActor` 的初值是 false（netStore 的 initial state），快照到达前它一直是 false。
   * 原来只看这一个标志，于是「刚进房、快照还没到就点离开」会被当成影子页：
   * 一个字都不发，人还留在房间里、座位还占着，用户以为自己走了。
   * `hasView` 为真才说明我们真的收到过这个房间的快照，「我不是 actor」这句话才作数。
   */
  if (isActor) return 'send'
  return hasView ? 'localOnly' : 'send'
}
